import { type Result } from 'neverthrow';
import { type z } from 'zod';

/**
 * Durable-data mode for an extension layout.
 *
 * `read-only` is the rollback mode for persisted extension data: existing views
 * can still be decoded and preserved, but new writes are blocked at mutation
 * boundaries. `off` means the layout is unsupported.
 */
export type ViewLayoutModuleMode = 'off' | 'read-only' | 'read-write';

/**
 * Core layout IDs that remain writable when no module overrides their mode.
 */
export const BUILT_IN_VIEW_LAYOUT_IDS = [
  'table',
  'gallery',
  'board',
  'calendar'
] as const;

export type BuiltInViewLayoutId = (typeof BUILT_IN_VIEW_LAYOUT_IDS)[number];

/**
 * Layout IDs supported by the legacy view-change write path.
 */
export const LEGACY_VIEW_LAYOUT_IDS = [...BUILT_IN_VIEW_LAYOUT_IDS, 'timeline'] as const;

export type LegacyViewLayoutId = (typeof LEGACY_VIEW_LAYOUT_IDS)[number];

/**
 * Compact storage codes that built-in layouts are persisted as in the
 * collaborative document (see `TypeMapping` in `@databases/collab` `y-view.ts`).
 *
 * These codes — not the canonical layout names — are the only real
 * decode-collision hazard. YView stores a built-in layout as its compact code
 * (`table` → `t`) and any other layout id verbatim in the same field, so an id
 * equal to a compact code would be silently decoded back as that built-in.
 * Registering a module under a compact code is therefore rejected (see
 * `defineLayoutModule` and `createViewLayoutRegistry`).
 *
 * Canonical layout names (`table`, `gallery`, `board`, `calendar`, `timeline`)
 * are intentionally NOT reserved: a built-in never persists as its own name, so
 * a module may register under a canonical name to own that layout's typed
 * settings. This is how core layouts — and first-party layouts like `timeline`
 * (which persists verbatim as `timeline`, not a compact code) — get a typed
 * properties module without adding a column to YView.
 *
 * Keep this set in sync with `TypeMapping` / `YTypeMapping` in `y-view.ts` if a
 * built-in's compact code ever changes.
 */
export const RESERVED_LAYOUT_STORAGE_CODES: ReadonlySet<string> = new Set([
  't',
  'g',
  'b',
  'cal',
  'c'
]);

/**
 * Whether `id` is a reserved built-in storage code and therefore cannot be used
 * as a layout-module id. See {@link RESERVED_LAYOUT_STORAGE_CODES}.
 */
export function isReservedLayoutTypeId(id: string): boolean {
  return RESERVED_LAYOUT_STORAGE_CODES.has(id);
}

/**
 * Converter that owns a layout's canonical defaults and its fail-safe
 * read/write transforms between stored data and the typed runtime shape.
 *
 * Generated by `defineLayoutModule` from the Zod schema; the SDK is the only
 * caller. Defaults come from the schema's field-level `.default()` values,
 * so adding a new defaulted property is a one-line schema change.
 *
 * @typeParam Props - Typed runtime shape of the layout's properties.
 */
export interface PropertiesStorageConverter<Props extends Record<string, unknown>> {
  /**
   * Canonical defaults for this shape, derived from the schema. Used for
   * first-time creation and to fill missing keys on read.
   */
  readonly defaults: Props;

  /**
   * Maps stored data into the typed runtime value. Must never throw:
   * missing keys are filled from defaults, and invalid data falls back to
   * defaults so callers always receive a valid `Props`.
   */
  fromDatabase(stored: Record<string, unknown> | undefined): Props;

  /**
   * Maps a typed runtime value into the persistable storage shape. Runs
   * before every write.
   */
  toDatabase(value: Props): Record<string, unknown>;
}

export type ViewLayoutModuleContext = {
  contentId?: string;
};

/**
 * UI-neutral metadata for opting a layout into layout switchers.
 *
 * Availability must be a pure eligibility check. Experiment exposure belongs
 * to the presentation surface after it has confirmed that the option is
 * visible; the registry has no UI lifecycle from which to fire it correctly.
 */
export type ViewLayoutSwitcherDefinition = {
  persistence: 'legacy-view-change' | 'sdk';
  isAvailable?: () => boolean;
};

/**
 * A switcher option with its required persistence path.
 */
export type ViewLayoutSwitcherOption =
  | { id: LegacyViewLayoutId; persistence: 'legacy-view-change' }
  | { id: string; persistence: 'sdk' };

export type ViewLayoutRegistryErrorReason =
  | 'VIEW_LAYOUT_NOT_FOUND'
  | 'VIEW_LAYOUT_WRITE_DISABLED'
  /** Layout is registered, but the supplied properties failed schema validation. */
  | 'VIEW_LAYOUT_INVALID_PROPERTIES';

export type ViewLayoutRegistryError = {
  layoutId: string;
  reason: ViewLayoutRegistryErrorReason;
  message: string;
};

/**
 * Data-module contract for registering a non-built-in layout type.
 *
 * Each module declares its property shape via a Zod schema and a
 * {@link PropertiesStorageConverter} that owns defaults and fail-safe read/write
 * transforms. The `schema` is publicly exposed so future UI tooling
 * (form generators, toolbar menus) can introspect it.
 *
 * @typeParam LayoutId - String literal identifying this layout (e.g. `'chart'`).
 * @typeParam Props    - Typed runtime shape of the layout's properties.
 */
export interface ViewLayoutDataModule<
  LayoutId extends string = string,
  Props extends Record<string, unknown> = Record<string, unknown>
> {
  readonly id: LayoutId;
  getMode(ctx: ViewLayoutModuleContext): ViewLayoutModuleMode;

  /**
   * Extension modules remain hidden from switchers unless this is declared.
   */
  readonly layoutSwitcher?: ViewLayoutSwitcherDefinition;

  readonly schema: z.ZodType<Props>;

  readonly converter: PropertiesStorageConverter<Props>;

  /**
   * Retained for backward compatibility with untyped callers.
   * Modules created via `defineLayoutModule` auto-generate this from
   * the schema.
   */
  parseProperties?(
    properties: unknown
  ): Result<Props | undefined, ViewLayoutRegistryError>;
}

/**
 * Extracts a `{ [layoutId]: Props }` map from a readonly tuple of modules.
 *
 * ```ts
 * const modules = [chartModule, dashboardModule] as const;
 * type Map = InferPropertiesMap<typeof modules>;
 * // { chart: ChartProperties; dashboard: DashboardProperties }
 * ```
 */
export type InferPropertiesMap<
  M extends readonly ViewLayoutDataModule<string, Record<string, unknown>>[]
> = {
  [
    Mod in M[number] as Mod extends ViewLayoutDataModule<
      infer Id,
      Record<string, unknown>
    >
      ? Id
      : never
  ]: Mod extends ViewLayoutDataModule<string, infer P> ? P : never;
};

/**
 * Typed registry that carries a layout-to-property type map.
 *
 * The registry's job is mode checks, module lookup, and write policy.
 * It does **not** expose conversion methods — the SDK uses
 * `getModule(layoutType).converter` internally. The `Map` generic exists
 * solely so the SDK can infer typed overloads from the registry.
 */
export interface TypedViewLayoutRegistry<
  Map extends Record<string, Record<string, unknown>> = Record<
    string,
    Record<string, unknown>
  >
> {
  /**
   * Type-only marker that carries the layout-id → properties-shape map so the
   * SDK can **infer** its typed property overloads directly from a registry
   * value (e.g. `new DatabaseSdk(ydb, { viewLayoutRegistry })` picks up the map
   * without an explicit type argument). Never assigned or read at runtime.
   */
  readonly __propertiesMap?: Map;

  getMode(layoutId: string): ViewLayoutModuleMode;
  getModule(layoutId: string): ViewLayoutDataModule | undefined;
  isKnown(layoutId: string): boolean;
  isWritable(layoutId: string): boolean;
  listWritableLayouts(): string[];
  listLayoutSwitcherOptions(): ViewLayoutSwitcherOption[];
  parseProperties(
    layoutId: string,
    properties: unknown
  ): Result<Record<string, unknown> | undefined, ViewLayoutRegistryError>;
  requireWritable(layoutId: string): Result<void, ViewLayoutRegistryError>;
}

export type ViewLayoutId = string;

/**
 * Base registry interface for untyped callers. {@link TypedViewLayoutRegistry}
 * adds a generic type-map parameter so the SDK can infer typed overloads.
 */
export interface ViewLayoutRegistry {
  getMode(layoutId: string): ViewLayoutModuleMode;
  getModule(layoutId: string): ViewLayoutDataModule | undefined;
  isKnown(layoutId: string): boolean;
  isWritable(layoutId: string): boolean;
  listWritableLayouts(): string[];
  listLayoutSwitcherOptions(): ViewLayoutSwitcherOption[];
  parseProperties(
    layoutId: string,
    properties: unknown
  ): Result<Record<string, unknown> | undefined, ViewLayoutRegistryError>;
  requireWritable(layoutId: string): Result<void, ViewLayoutRegistryError>;
}
